import { stitchReasoningChunks } from './stitchReasoningChunks';

export interface ParsedReasoning {
  statusText: string;
  steps: ReadonlyArray<string>;
}

export interface ParsedMessage {
  reasoning: ParsedReasoning | null;
  response: string;
}

const REASONING_SEPARATOR = /\n---\n/;
const LEGACY_REASONING_INDICATOR_PATTERN =
  /(^|\n)(Query is valid\.?|The user\b|Using tool:|Processing results|Synthesising results|Task \d+:|Parsing filters:)/i;
const FILTER_LINE_PATTERN = /^(.+?)\s*Parsing filters:\s*.+$/;
const UPPERCASE_CHARACTER_PATTERN = /\p{Lu}/u;
const SENTENCE_CLOSER_PATTERN = /["')\]}]/;
const STRUCTURAL_BULLET_PATTERN = /^[-*]\s+/;
const STANDALONE_ORDERED_MARKER_PATTERN = /^\d+[.)]$/;

function stripStructuralListMarker(line: string): string {
  return line.replace(STRUCTURAL_BULLET_PATTERN, '').trim();
}

function normalizeReasoningLines(rawLines: ReadonlyArray<string>): ReadonlyArray<string> {
  return rawLines.reduce<string[]>((result, line) => {
    const normalizedLine = stripStructuralListMarker(line);
    const previousLine = result.at(-1);

    if (previousLine && STANDALONE_ORDERED_MARKER_PATTERN.test(previousLine)) {
      result[result.length - 1] = `${previousLine} ${normalizedLine}`;
      return result;
    }

    result.push(normalizedLine);
    return result;
  }, []);
}

function splitIntoSentences(text: string): ReadonlyArray<string> {
  const sentences: string[] = [];
  let startIndex = 0;
  let index = 0;

  while (index < text.length) {
    const currentCharacter = text.charAt(index);
    if (!/[.!?]/.test(currentCharacter)) {
      index += 1;
      continue;
    }

    if (currentCharacter === '.' && /^\d+$/.test(text.slice(startIndex, index).trim())) {
      index += 1;
      continue;
    }

    let lookaheadIndex = index + 1;
    while (lookaheadIndex < text.length && /[.!?]/.test(text.charAt(lookaheadIndex))) {
      lookaheadIndex += 1;
    }

    while (lookaheadIndex < text.length && SENTENCE_CLOSER_PATTERN.test(text.charAt(lookaheadIndex))) {
      lookaheadIndex += 1;
    }

    const nextCharacter = text.charAt(lookaheadIndex);
    const isSentenceBoundary =
      lookaheadIndex >= text.length || /\s/.test(nextCharacter) || UPPERCASE_CHARACTER_PATTERN.test(nextCharacter);

    if (!isSentenceBoundary) {
      index = lookaheadIndex;
      continue;
    }

    const sentence = text.slice(startIndex, lookaheadIndex).trim();
    if (sentence) {
      sentences.push(sentence);
    }

    startIndex = lookaheadIndex;
    while (startIndex < text.length && /\s/.test(text.charAt(startIndex))) {
      startIndex += 1;
    }

    index = startIndex;
  }

  const remainder = text.slice(startIndex).trim();
  if (remainder) {
    sentences.push(remainder);
  }

  return sentences;
}

function splitReasoningLines(text: string): ReadonlyArray<string> {
  const rawLines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  return normalizeReasoningLines(rawLines).flatMap(splitIntoSentences);
}

function isLegacyReasoningBlock(text: string): boolean {
  return LEGACY_REASONING_INDICATOR_PATTERN.test(text);
}

function parseReasoning(reasoningText: string, splitTrailingLines: boolean): ParsedReasoning | null {
  const rawLines = reasoningText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  const lines = normalizeReasoningLines(rawLines);

  if (lines.length === 0) {
    return null;
  }

  const [firstLine, ...remainingLines] = lines;
  const filterMatch = FILTER_LINE_PATTERN.exec(firstLine);

  if (filterMatch) {
    const prefix = filterMatch[1].trim();
    const prefixParts = splitIntoSentences(prefix);
    const statusText = prefixParts[0] ?? '';
    const trailingSteps = splitTrailingLines ? remainingLines.flatMap(splitIntoSentences) : remainingLines;
    const steps = [...prefixParts.slice(1), ...trailingSteps];

    return {
      statusText,
      steps,
    };
  }

  if (remainingLines.length > 0) {
    const [statusText, ...initialSteps] = splitIntoSentences(firstLine);
    const trailingSteps = splitTrailingLines ? remainingLines.flatMap(splitIntoSentences) : remainingLines;

    if (!statusText) {
      return null;
    }

    return {
      statusText,
      steps: [...initialSteps, ...trailingSteps],
    };
  }

  const [statusText, ...steps] = splitReasoningLines(firstLine);

  if (!statusText) {
    return null;
  }

  return {
    statusText,
    steps,
  };
}

export function parseMessageContent(content: string): ParsedMessage {
  const separatorMatch = REASONING_SEPARATOR.exec(content);

  if (!separatorMatch) {
    return { reasoning: null, response: content };
  }

  const reasoningBlock = content.slice(0, separatorMatch.index);
  const response = content.slice(separatorMatch.index + separatorMatch[0].length);

  if (!isLegacyReasoningBlock(reasoningBlock)) {
    return { reasoning: null, response: content };
  }

  return {
    reasoning: parseReasoning(reasoningBlock, false) ?? { statusText: '', steps: [] },
    response,
  };
}

export function buildReasoning(reasoningSteps: ReadonlyArray<string>): ParsedReasoning | null {
  return parseReasoning(stitchReasoningChunks(reasoningSteps), true);
}
